import { type Query, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { isEmailVerified, logout } from "#/lib/auth-client";
import { ANONYMOUS_SESSION } from "#/lib/medusa-types/auth";
import { notify } from "#/lib/notify";
import { ROUTES } from "#/lib/routes";
import { sessionKeys, sessionQueries } from "#/lib/useSession";
import { useCartStore } from "#/store/cart";
import { PageHeader } from "../components/primitives.js";

const CUSTOMER_SCOPED_KEY_NAMESPACES: ReadonlySet<string> = new Set([
  "orders",
  "customer",
  "wishlist",
  "addresses",
]);

export function isCustomerScopedQuery(query: Query): boolean {
  const key = query.queryKey;
  if (!Array.isArray(key) || key.length < 2) return false;
  const [root, namespace] = key;
  if (root !== "storefront" || typeof namespace !== "string") return false;
  return CUSTOMER_SCOPED_KEY_NAMESPACES.has(namespace);
}

export const Route = createFileRoute("/account")({
  head: () => ({ meta: [{ title: "My Account" }] }),
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.ensureQueryData(
      sessionQueries.current(),
    );
    if (!session.customer || !isEmailVerified(session.customer)) {
      throw redirect({
        to: ROUTES.login,
        search: { redirect: location.href },
      });
    }
  },
  component: AccountLayout,
});

const TABS: ReadonlyArray<{ to: string; label: string }> = [
  { to: ROUTES.accountOrders, label: "My Orders" },
  { to: ROUTES.accountWishlist, label: "My Wishlist" },
  { to: ROUTES.accountAddresses, label: "Addresses" },
  { to: ROUTES.accountProfile, label: "Profile" },
];

function AccountLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const clearCart = useCartStore((s) => s.clearCart);

  const logoutMutation = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      notify.success("Signed out");
    },
    onError: (err) => {
      notify.error(
        err instanceof Error ? err.message : "Could not sign out. Try again.",
      );
    },
    onSettled: () => {
      // Always clear local session state, even if the server logout call
      // failed — otherwise the UI is stuck showing a logged-in shell while
      // the user has clicked "log out". Predicate-based removal purges
      // customer-scoped caches (orders, addresses, wishlist, customer
      // detail) so the next signed-in user on a shared device never sees
      // the prior user's data, even for one render frame. The session
      // pointer is reset after — it must survive the removeQueries pass.
      queryClient.removeQueries({ predicate: isCustomerScopedQuery });
      queryClient.setQueryData(sessionKeys.current, ANONYMOUS_SESSION);
      clearCart();
      navigate({ to: ROUTES.home });
    },
  });

  return (
    <div data-screen-label="Account">
      <PageHeader
        title="My Account"
        subtitle="Manage your orders, wishlist and profile."
      />
      <div className="mx-auto grid w-full max-w-figma grid-cols-1 lg:grid-cols-[260px_1fr] gap-12 px-10 py-16">
        <aside className="flex flex-col gap-2">
          {TABS.map((t) => {
            const active = pathname === t.to;
            return (
              <Link
                key={t.to}
                to={t.to}
                className={`rounded-full px-5 py-3 text-left font-display text-base no-underline transition ${
                  active
                    ? "bg-lp-green text-white"
                    : "bg-transparent text-lp-text hover:bg-lp-green-soft"
                }`}
              >
                {t.label}
              </Link>
            );
          })}
          <button
            type="button"
            onClick={() => {
              logoutMutation.mutate(undefined, {
                onSuccess: () => {
                  navigate({ to: "/login", replace: true });
                },
              });
            }}
            disabled={logoutMutation.isPending}
            className="mt-4 cursor-pointer rounded-full border-0 bg-transparent px-5 py-3 text-left font-display text-base text-lp-text-lt hover:bg-lp-grey disabled:cursor-default disabled:opacity-60"
          >
            {logoutMutation.isPending ? "Signing out…" : "Log out"}
          </button>
        </aside>
        <div>
          <Outlet />
        </div>
      </div>
    </div>
  );
}
